import React, { useEffect, useState } from "react";
import { Globe, FileSpreadsheet } from "lucide-react";
import api from "../../../shared/services/apiClient";
import toast from "react-hot-toast";

type DataSource = {
  id?: number;
  name: string;
  description: string;
  type: string;
  records: number;
  fields: number;
  lastUpdate?: string;
  status?: string;
  spreadsheetId?: string;
  sheetName?: string;
  range?: string;
  autoWidgets?: any[];
  configJson?: any;
};

type ApiRestConfig = {
  baseUrl: string;
  endpoint: string;
  method: string;
  headers?: Record<string, string>;
  body?: string;
};

type KeyValuePair = {
  id: string;
  key: string;
  value: string;
  enabled: boolean;
};

type DynamicParams = {
  query: KeyValuePair[];
  path: KeyValuePair[];
  headers: KeyValuePair[];
  body: KeyValuePair[];
};

type AuthConfig = {
  type: 'none' | 'bearer' | 'oauth';
  token: string;
  tokenUrl?: string;
  clientId?: string;
  clientSecret?: string;
  scope?: string;
};

type Props = {
  datasource?: DataSource;
  presetType?: string;
  currentUser?: any;
  onSave: (data: DataSource) => void;
  onClose: () => void;
};

// Only Google Sheets and Base local - API REST uses SimplifiedApiForm
const types = ["Google Sheets", "Base local"];

// Map UI types to Prisma enum types
const TYPE_MAP: Record<string, string> = {
  "Google Sheets": "GOOGLE_SHEETS",
  "Base local": "SQL_POSTGRES",
};

// Reverse map: Prisma enum to UI display names (kept for backward compatibility when loading existing datasources)
const REVERSE_TYPE_MAP: Record<string, string> = {
  "GOOGLE_SHEETS": "Google Sheets",
  "API_REST": "API REST", // Kept only for loading existing datasources, not for creating new ones
  "SQL_POSTGRES": "Base local",
  "SQL_MYSQL": "Base local",
  "SQL_SQLSERVER": "Base local",
  "EXCEL": "Base local",
  "CSV": "Base local",
};

// Map UI status to Prisma enum Status
const STATUS_MAP: Record<string, string> = {
  "active": "ACTIVE",
  "inactive": "INACTIVE",
};

const DatasourceForm: React.FC<Props> = ({ datasource, presetType, currentUser, onSave, onClose }) => {
  const [form, setForm] = useState<DataSource>({
    name: "",
    description: "",
    type: presetType || "Google Sheets",
    records: 0,
    fields: 0,
    status: "inactive",
    spreadsheetId: "",
    sheetName: "Sheet1",
    range: "",
    configJson: {},
  });

  // 🎯 Business Context (optional semantic description)
  const [businessContext, setBusinessContext] = useState<string>("");

  const [saving, setSaving] = useState(false);

  // API REST specific state
  const [apiConfig, setApiConfig] = useState<ApiRestConfig>({
    baseUrl: "",
    endpoint: "",
    method: "GET",
    headers: {},
    body: "",
  });

  const [dynamicParams, setDynamicParams] = useState<DynamicParams>({
    query: [],
    path: [],
    headers: [],
    body: [],
  });

  // 🎯 API Type Selection (Público/Privado)
  const [apiType, setApiType] = useState<'public' | 'private'>('public');
  
  // 🔐 Private API Subtype Selection
  const [privateApiMode, setPrivateApiMode] = useState<'simple' | 'oauth_formal'>('simple');
  
  // 📊 Authentication Phase Control
  const [authPhase, setAuthPhase] = useState<'configure' | 'validated' | 'data_config'>('configure');
  const [savedAuthToken, setSavedAuthToken] = useState<string | null>(null);
  const [tokenExpiration, setTokenExpiration] = useState<Date | null>(null);

  // 🔹 TOKEN SIMPLE Config (Postman-style)
  const [simpleAuthConfig, setSimpleAuthConfig] = useState({
    method: 'GET' as 'GET' | 'POST',
    tokenUrl: '',
    queryParams: [] as KeyValuePair[],
    headers: [] as KeyValuePair[],
    body: [] as KeyValuePair[]
  });

  // 🔹 OAUTH FORMAL Config (Standard)
  const [oauthConfig, setOauthConfig] = useState({
    tokenUrl: '',
    clientId: '',
    clientSecret: '',
    scope: ''
  });

  const [authConfig, setAuthConfig] = useState<AuthConfig>({
    type: 'none',
    token: '',
  });

  const [activeParamTab, setActiveParamTab] = useState<'query' | 'path' | 'headers' | 'body'>('query');
  const [activeAuthTab, setActiveAuthTab] = useState<'query' | 'headers' | 'body'>('query');
  const [detectedType, setDetectedType] = useState<string | null>(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [preview, setPreview] = useState<any[]>([]);
  const [autoWidgets, setAutoWidgets] = useState<any[]>([]);
  
  // API REST testing state
  const [testingApi, setTestingApi] = useState(false);
  const [apiTestSuccess, setApiTestSuccess] = useState(false);
  const [apiPreview, setApiPreview] = useState<any[]>([]);

  // ✅ FEATURE: ENABLE MULTI-SHEET SUPPORT
  const ENABLE_MULTI_SOURCE_UI = true; // Multiple sheets from same spreadsheet

  // Multiple Google Sheets state
  const [isMultipleSheets, setIsMultipleSheets] = useState(false);
  const [googleSheets, setGoogleSheets] = useState<Array<{
    id: string;
    spreadsheetId: string;
    sheetName: string;
    range: string;
    alias: string;
  }>>([{
    id: Date.now().toString(),
    spreadsheetId: '',
    sheetName: 'Sheet1',
    range: '',
    alias: ''
  }]);

  useEffect(() => {
    if (datasource) {
      console.log('🔍 Loading datasource for edit:', datasource);
      
      // Convert database type to UI display name
      const displayType = REVERSE_TYPE_MAP[datasource.type] || datasource.type;
      
      // Set base form data
      setForm({
        ...datasource,
        type: displayType
      });
      
      // Load Google Sheets config if exists
      if ((datasource.type === "GOOGLE_SHEETS" || displayType === "Google Sheets")) {
        const config = (datasource as any).config || (datasource as any).configJson || {};
        
        console.log('📋 Loading Google Sheets config:', config);
        
        // Check if it's multiple sheets configuration
        if (config.type === 'GOOGLE_SHEETS_MULTIPLE' || config.multiple?.sheets) {
          // 🚫 FORCE SINGLE-SOURCE MODE - Never enable multi-source UI
          console.log('🚫 Multi-source config detected but UI disabled for MVP, converting to single-source');
          
          // Extract first sheet as single source
          const firstSheet = config.multiple?.sheets?.[0] || config.sheets?.[0];
          if (firstSheet) {
            setForm(prev => ({
              ...prev,
              spreadsheetId: firstSheet.spreadsheetId || '',
              sheetName: firstSheet.sheetName || 'Sheet1',
              range: firstSheet.range || ''
            }));
            console.log('🔄 Converted multi-source to single-source for editing');
          }
        } else {
          // Single sheet configuration
          setIsMultipleSheets(false);
          setForm(prev => ({
            ...prev,
            spreadsheetId: config.spreadsheetId || datasource.spreadsheetId || '',
            sheetName: config.sheetName || datasource.sheetName || 'Sheet1',
            range: config.range || datasource.range || ''
          }));
          console.log('📄 Loaded single sheet config');
        }
        
        // 🎯 Load business context if exists
        if (config.businessContext) {
          setBusinessContext(config.businessContext);
          console.log('📋 Loaded business context');
        }
        
        // Check if already analyzed
        if ((datasource as any).analysis && datasource.status === 'ACTIVE') {
          setAnalyzed(true);
          console.log('✅ Datasource already analyzed');
        }
      }
      
      // Load API REST config if exists
      if ((datasource.type === "API_REST" || datasource.type === "API REST") && datasource.configJson) {
        const config = datasource.configJson;
        setApiConfig({
          baseUrl: config.baseUrl || "",
          endpoint: config.endpoint || "",
          method: config.method || "GET",
          headers: config.headers || {},
          body: config.body || "",
        });

        // 🎯 Load business context if exists
        if (config.businessContext) {
          setBusinessContext(config.businessContext);
          console.log('📋 Loaded business context from API REST config');
        }

        // Load dynamic parameters
        if (config.parameters) {
          const params = config.parameters;
          setDynamicParams({
            query: objectToKeyValuePairs(params.query || {}),
            path: objectToKeyValuePairs(params.path || {}),
            headers: objectToKeyValuePairs(params.headers || {}),
            body: objectToKeyValuePairs(params.body || {}),
          });
        }

        // Load auth config
        if (config.auth) {
          setAuthConfig({
            type: config.auth.type || 'none',
            token: config.auth.token || '',
          });
        }
      }
    } else if (presetType) {
      setForm(prev => ({ ...prev, type: presetType }));
    }
  }, [datasource, presetType]);

  // Helper function to convert object to KeyValuePair array
  const objectToKeyValuePairs = (obj: Record<string, any>): KeyValuePair[] => {
    return Object.entries(obj).map(([key, value], index) => ({
      id: `${Date.now()}-${index}`,
      key,
      value: String(value),
      enabled: true,
    }));
  };

  // Helper function to convert KeyValuePair array to object
  const keyValuePairsToObject = (pairs: KeyValuePair[]): Record<string, any> => {
    const obj: Record<string, any> = {};
    pairs.forEach(pair => {
      if (pair.enabled && pair.key.trim()) {
        obj[pair.key.trim()] = pair.value;
      }
    });
    return obj;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "records" || name === "fields" ? Number(value) : value,
    }));
  };

  const handleApiConfigChange = (field: keyof ApiRestConfig, value: any) => {
    setApiConfig(prev => ({
      ...prev,
      [field]: value,
    }));

    // Auto-detect type when baseUrl changes
    if (field === 'baseUrl') {
      const detected = detectDataSourceType(value);
      if (detected) {
        setDetectedType(detected.displayName);
        setForm(prev => ({
          ...prev,
          type: detected.type,
        }));
      } else {
        setDetectedType(null);
      }
    }
  };

  // Dynamic params management
  const addParam = (type: keyof DynamicParams) => {
    setDynamicParams(prev => ({
      ...prev,
      [type]: [...prev[type], {
        id: `${Date.now()}-${Math.random()}`,
        key: '',
        value: '',
        enabled: true,
      }],
    }));
  };

  const updateParam = (type: keyof DynamicParams, id: string, field: 'key' | 'value' | 'enabled', value: any) => {
    setDynamicParams(prev => ({
      ...prev,
      [type]: prev[type].map(param =>
        param.id === id ? { ...param, [field]: value } : param
      ),
    }));
  };

  const removeParam = (type: keyof DynamicParams, id: string) => {
    setDynamicParams(prev => ({
      ...prev,
      [type]: prev[type].filter(param => param.id !== id),
    }));
  };

  // Multiple Google Sheets handlers
  const addGoogleSheet = () => {
    setGoogleSheets(prev => [...prev, {
      id: Date.now().toString(),
      spreadsheetId: '',
      sheetName: 'Sheet1',
      range: '',
      alias: ''
    }]);
  };

  const removeGoogleSheet = (id: string) => {
    if (googleSheets.length > 1) {
      setGoogleSheets(prev => prev.filter(sheet => sheet.id !== id));
    }
  };

  const updateGoogleSheet = (id: string, field: string, value: string) => {
    setGoogleSheets(prev => prev.map(sheet => 
      sheet.id === id ? { ...sheet, [field]: value } : sheet
    ));
  };

  const extractSpreadsheetIdFromInput = (input?: string) => {
    if (!input) return '';
    const s = input.trim();
    // Try to match /d/{id}
    const m = s.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (m && m[1]) return m[1];
    // Fallback: any long-ish token (20+ chars) of allowed chars
    const fallback = s.match(/([a-zA-Z0-9-_]{20,})/);
    if (fallback && fallback[1]) return fallback[1];
    return s;
  };

  // Auto-detect data source type based on URL patterns
  const detectDataSourceType = (url: string): { type: string; displayName: string } | null => {
    if (!url) return null;

    const lowerUrl = url.toLowerCase();

    // Weather API detection
    if (lowerUrl.includes('open-meteo')) {
      return { type: 'API REST', displayName: 'Weather API (Open-Meteo)' };
    }

    // Google API detection
    if (lowerUrl.includes('googleapis')) {
      return { type: 'API REST', displayName: 'Google API' };
    }

    // CSV file detection
    if (lowerUrl.endsWith('.csv')) {
      return { type: 'Base local', displayName: 'CSV File' };
    }

    // Excel file detection
    if (lowerUrl.endsWith('.xlsx') || lowerUrl.endsWith('.xls')) {
      return { type: 'Base local', displayName: 'Excel File' };
    }

    // GraphQL detection
    if (lowerUrl.includes('/graphql')) {
      return { type: 'API REST', displayName: 'GraphQL API' };
    }

    // Default REST API
    if (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) {
      return { type: 'API REST', displayName: 'REST API' };
    }

    return null;
  };



  // Build preview URL with all parameters - POSTMAN STYLE
  const buildPreviewUrl = (): string => {
    if (!apiConfig.baseUrl) return '';

    try {
      let cleanBaseUrl = apiConfig.baseUrl.trim().replace(/\/$/, '');
      let endpoint = (apiConfig.endpoint || '').trim();

      // Replace path parameters
      dynamicParams.path.forEach(param => {
        if (param.enabled && param.key && param.value) {
          const colonParam = param.key.startsWith(':') ? param.key : `:${param.key}`;
          const braceParam = `{${param.key.replace(/^:/, '')}}`;
          endpoint = endpoint.replace(colonParam, param.value);
          endpoint = endpoint.replace(braceParam, param.value);
        }
      });

      // Use URL API to properly construct URL (NOT string concatenation)
      const url = new URL(cleanBaseUrl + endpoint);

      // Add query parameters using URL.searchParams
      const enabledQuery = dynamicParams.query.filter(p => p.enabled && p.key.trim());
      enabledQuery.forEach(param => {
        url.searchParams.append(param.key.trim(), param.value);
      });

      return url.href;
    } catch (error) {
      console.error('Invalid URL:', error);
      return 'Invalid URL';
    }
  };

  // � FUNCIÓN PARA TOKEN SIMPLE (Postman-style)
  const handleTestTokenSimple = async () => {
    if (!simpleAuthConfig.tokenUrl) {
      toast.error('Token URL es requerido');
      return;
    }

    setTestingApi(true);
    try {
      console.log('🔹 Testing Token Simple auth...');
      
      // � TOKEN SIMPLE FLEXIBLE - Respeta query params o body según configuración
      const bodyParams = keyValuePairsToObject(simpleAuthConfig.body);
      const queryParams = keyValuePairsToObject(simpleAuthConfig.queryParams);
      
      console.log('🔍 Query params:', queryParams);
      console.log('🔍 Body params:', bodyParams);
      
      // Token Simple maneja AMBOS casos: query params Y body params
      const payload = {
        type: "TOKEN_SIMPLE_TEST",
        config: {
          method: simpleAuthConfig.method,
          url: simpleAuthConfig.tokenUrl, 
          queryParams: queryParams,
          headers: keyValuePairsToObject(simpleAuthConfig.headers),
          body: bodyParams
        }
      };

      console.log('📤 Sending payload:', payload);

      const response = await fetch('/api/datasources/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('📥 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('📊 Parsed result:', result);

      if (result.success && result.data?.access_token) {
        setSavedAuthToken(result.data.access_token);
        setTokenExpiration(result.data.expires_at ? new Date(result.data.expires_at) : null);
        setAuthPhase('validated');
        toast.success('🟢 Token obtenido exitosamente!');
      } else {
        throw new Error(result.error || 'No se encontró access_token en la respuesta');
      }
    } catch (error: any) {
      console.error('Simple auth test error:', error);
      
      // 2️⃣ MANEJO ESPECÍFICO DE ERRORES DE PARSING
      if (error.message.includes('Unexpected token') || error.message.includes('<!DOCTYPE')) {
        toast.error('🔴 La API devolvió HTML en lugar de JSON. Verifica la URL del token.');
      } else if (error.message.includes('Failed to fetch')) {
        toast.error('🔴 No se pudo conectar con el servidor. Verifica la URL y que el backend esté corriendo.');
      } else {
        toast.error(`🔴 Error de autenticación: ${error.message}`);
      }
    } finally {
      setTestingApi(false);
    }
  };

  // 🔹 FUNCIÓN PARA OAUTH FORMAL (Estándar)
  const handleTestOAuthFormal = async () => {
    if (!oauthConfig.tokenUrl || !oauthConfig.clientId || !oauthConfig.clientSecret) {
      toast.error('Token URL, Client ID y Client Secret son requeridos');
      return;
    }

    setTestingApi(true);
    try {
      const payload = {
        type: "OAUTH_FORMAL_TEST",
        config: {
          tokenUrl: oauthConfig.tokenUrl,
          clientId: oauthConfig.clientId,
          clientSecret: oauthConfig.clientSecret,
          scope: oauthConfig.scope
        }
      };

      const response = await fetch('/api/datasources/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success && result.data.access_token) {
        setSavedAuthToken(result.data.access_token);
        setTokenExpiration(result.data.expires_at ? new Date(result.data.expires_at) : null);
        setAuthPhase('validated');
        toast.success('🟢 Autenticación OAuth exitosa!');
      } else {
        throw new Error(result.error || 'Autenticación OAuth fallida');
      }
    } catch (error: any) {
      console.error('OAuth formal test error:', error);
      toast.error(`🔴 Error OAuth: ${error.message}`);
    } finally {
      setTestingApi(false);
    }
  };

  const handleTestApiConnection = async () => {
    // 🔐 Para APIs privadas, verificar que la autenticación esté configurada
    if (apiType === 'private' && authPhase !== 'validated') {
      toast.error('Please configure and validate authentication first');
      return;
    }
    if (!apiConfig.baseUrl.trim() || !apiConfig.endpoint.trim()) {
      toast.error("Base URL y Endpoint son requeridos para probar la conexión");
      return;
    }

    setTestingApi(true);
    setApiTestSuccess(false);
    setApiPreview([]);

    try {
      // Build parameters object
      const parameters: any = {};
      
      const queryObj = keyValuePairsToObject(dynamicParams.query);
      if (Object.keys(queryObj).length > 0) parameters.query = queryObj;
      
      const pathObj = keyValuePairsToObject(dynamicParams.path);
      if (Object.keys(pathObj).length > 0) parameters.path = pathObj;
      
      const headersObj = keyValuePairsToObject(dynamicParams.headers);
      if (Object.keys(headersObj).length > 0) parameters.headers = headersObj;
      
      const bodyObj = keyValuePairsToObject(dynamicParams.body);
      if (Object.keys(bodyObj).length > 0) parameters.body = bodyObj;

      // 🔐 Build auth object para test basado en el tipo de API
      let auth: any = undefined;
      
      if (apiType === 'private' && savedAuthToken) {
        if (privateApiMode === 'simple') {
          auth = { 
            mode: 'simple',
            accessToken: savedAuthToken,
            tokenRequest: {
              method: simpleAuthConfig.method,
              url: simpleAuthConfig.tokenUrl,
              queryParams: keyValuePairsToObject(simpleAuthConfig.queryParams),
              headers: keyValuePairsToObject(simpleAuthConfig.headers),
              body: keyValuePairsToObject(simpleAuthConfig.body)
            }
          };
        } else {
          auth = { 
            mode: 'oauth_formal',
            accessToken: savedAuthToken,
            tokenUrl: oauthConfig.tokenUrl,
            clientId: oauthConfig.clientId,
            clientSecret: oauthConfig.clientSecret,
            scope: oauthConfig.scope
          };
        }
      }

      const testPayload = {
        type: "API_REST",
        baseUrl: apiConfig.baseUrl,
        endpoint: apiConfig.endpoint,
        method: apiConfig.method,
        parameters: Object.keys(parameters).length > 0 ? parameters : undefined,
        auth,
      };

      console.log('Testing API connection with dynamic params:', testPayload);
      const response = await api.post("/datasources/test", testPayload);
      
      toast.success("✅ Conexión exitosa!");
      setApiTestSuccess(true);
      
      // Store preview data if available
      if (response.data.preview && Array.isArray(response.data.preview)) {
        setApiPreview(response.data.preview.slice(0, 5));
      } else if (response.data.data && Array.isArray(response.data.data)) {
        setApiPreview(response.data.data.slice(0, 5));
      }

    } catch (error: any) {
      console.error("Error testing API connection:", error);
      const message = error.response?.data?.message || error.message || "Error al conectar con la API";
      toast.error(`❌ Error: ${message}`);
    } finally {
      setTestingApi(false);
    }
  };

  const handleAnalyze = async () => {
    if (!form.name?.trim()) {
      toast.error("Primero ingresa un nombre para la fuente de datos");
      return;
    }
    
    if (!form.spreadsheetId?.trim()) {
      toast.error("Ingresa el ID o URL del Google Sheet");
      return;
    }

    if (form.type === "API REST") {
      if (!apiConfig.baseUrl.trim() || !apiConfig.endpoint.trim()) {
        toast.error("Base URL y Endpoint son requeridos para API REST");
        return;
      }
    }

    // Validate empresaId
    const empresaId = currentUser?.empresaId || currentUser?.empresa?.id;
    if (!empresaId) {
      console.error('❌ currentUser sin empresaId:', currentUser);
      toast.error("Error: Usuario sin empresa asignada. Por favor, contacta al administrador.");
      return;
    }

    setAnalyzing(true);
    try {
      // Primero crear/actualizar el datasource
      let dsId = form.id;
      if (!dsId) {
        console.log('Creando datasource con empresaId:', empresaId);
        const cleanedSpreadsheetId = extractSpreadsheetIdFromInput(form.spreadsheetId);
        const payload: any = {
          empresaId,
          name: form.name,
          description: form.description,
          type: TYPE_MAP[form.type] || form.type,
          status: "active",
          // Provide explicit config to ensure backend receives spreadsheetId
          config: {
            spreadsheetId: cleanedSpreadsheetId || null,
            sheetName: form.sheetName,
            range: form.range || null,
          },
          // Keep top-level for backwards compatibility
          spreadsheetId: cleanedSpreadsheetId || form.spreadsheetId,
        };

        const response = await api.post("/datasources", payload);
        dsId = response.data.id;
        console.log('Datasource creado con ID:', dsId);
        console.log('🔍 Backend response:', response.data);
        setForm(prev => ({ ...prev, id: dsId }));

        // Check if auto-analysis was successful
        if (response.data.autoAnalyzed && response.data.analysis) {
          console.log('✅ Auto-analysis succeeded, skipping manual analysis');
          
          // Extract analysis data from the auto-analysis
          const analysis = response.data.analysis;
          const profile = analysis.unifiedProfile || { rowCount: analysis.analyzedRows || 0, columns: [] };
          
          toast.success("DataSource creada y analizada automáticamente");
          setAnalyzed(true);
          setPreview([]); // Will be populated from analysis if needed
          setAutoWidgets([]); // Will be populated from analysis if needed
          
          // Update form with analyzed data
          setForm(prev => ({
            ...prev,
            records: profile.rowCount || 0,
            fields: profile.columns?.length || 0,
          }));
          
          return; // Exit early, no need to call /analyze endpoint
        }
        
        console.log('⚠️ Auto-analysis failed or incomplete, proceeding with manual analysis');
      }

      // Analizar el sheet
      console.log('Analizando sheet con ID:', dsId);
      const analyzeResponse = await api.post(`/datasources/${dsId}/analyze`);
      const { profile, autoWidgets: widgets, preview: previewData, message, name: returnedName, description: returnedDescription } = analyzeResponse.data;

      toast.success(message || "Análisis completado");
      setAnalyzed(true);
      setPreview(previewData || []);
      setAutoWidgets(widgets || []);
      
      // Actualizar form con datos analizados
      setForm(prev => ({
        ...prev,
        name: returnedName || prev.name,
        description: returnedDescription || prev.description,
        records: profile.rowCount || 0,
        fields: profile.columns?.length || 0,
      }));

    } catch (error: any) {
      console.error("Error analyzing:", error);
      toast.error(error.response?.data?.message || "Error analizando el Google Sheet");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre de la fuente es obligatorio");
      return;
    }
    
    // Validation for Google Sheets (SINGLE SOURCE ONLY FOR MVP)
    if (form.type === "Google Sheets") {
      // Single sheet validation - just need valid URL
      if (!form.spreadsheetId?.trim()) {
        toast.error("Debes ingresar una URL o ID de Google Sheets válido");
        return;
      }
      console.log('✅ Single Google Sheets validation passed');
    }

    if (form.type === "API REST") {
      if (!apiConfig.baseUrl.trim() || !apiConfig.endpoint.trim()) {
        toast.error("Base URL y Endpoint son requeridos para API REST");
        return;
      }

      // Para APIs privadas, verificar que la autenticación esté validada
      if (apiType === 'private' && authPhase !== 'validated') {
        toast.error("Debes configurar y validar la autenticación OAuth primero");
        return;
      }

      // Validate empresaId
      const empresaId = currentUser?.empresaId || currentUser?.empresa?.id;
      if (!empresaId) {
        console.error('❌ currentUser sin empresaId:', currentUser);
        toast.error("Error: Usuario sin empresa asignada. Por favor, contacta al administrador.");
        return;
      }

      // Create API REST datasource via backend
      try {
        // Build parameters object
        const parameters: any = {};
        
        const queryObj = keyValuePairsToObject(dynamicParams.query);
        if (Object.keys(queryObj).length > 0) parameters.query = queryObj;
        
        const pathObj = keyValuePairsToObject(dynamicParams.path);
        if (Object.keys(pathObj).length > 0) parameters.path = pathObj;
        
        const headersObj = keyValuePairsToObject(dynamicParams.headers);
        if (Object.keys(headersObj).length > 0) parameters.headers = headersObj;
        
        const bodyObj = keyValuePairsToObject(dynamicParams.body);
        if (Object.keys(bodyObj).length > 0) parameters.body = bodyObj;

        // 🔐 Build auth object basado en el tipo de API y modo de autenticación
        let auth: any = undefined;
        
        if (apiType === 'private' && savedAuthToken) {
          if (privateApiMode === 'simple') {
            // Token Simple mode - estilo Postman
            auth = { 
              mode: 'simple',
              tokenRequest: {
                method: simpleAuthConfig.method,
                url: simpleAuthConfig.tokenUrl,
                queryParams: keyValuePairsToObject(simpleAuthConfig.queryParams),
                headers: keyValuePairsToObject(simpleAuthConfig.headers),
                body: keyValuePairsToObject(simpleAuthConfig.body)
              },
              accessToken: savedAuthToken,
              expiresAt: tokenExpiration?.toISOString()
            };
          } else if (privateApiMode === 'oauth_formal') {
            // OAuth Formal mode - estándar
            auth = { 
              mode: 'oauth_formal',
              tokenUrl: oauthConfig.tokenUrl,
              clientId: oauthConfig.clientId,
              clientSecret: oauthConfig.clientSecret,
              scope: oauthConfig.scope,
              accessToken: savedAuthToken,
              expiresAt: tokenExpiration?.toISOString()
            };
          }
        }

        const payload = {
          empresaId,
          name: form.name,
          description: form.description,
          type: TYPE_MAP[form.type] || "API_REST",
          // Send fields at top level for Prisma schema
          baseUrl: apiConfig.baseUrl,
          endpoint: apiConfig.endpoint,
          method: apiConfig.method,
          parameters: Object.keys(parameters).length > 0 ? parameters : undefined,
          auth,
          // Also include in configJson for backwards compatibility
          configJson: {
            baseUrl: apiConfig.baseUrl,
            endpoint: apiConfig.endpoint,
            method: apiConfig.method,
            parameters: Object.keys(parameters).length > 0 ? parameters : undefined,
            auth,
            businessContext: businessContext.trim() || undefined, // 🎯 Include business context
          },
        };

        console.log('Creating API REST datasource with dynamic params:', payload);
        const response = await api.post("/datasources", payload);
        
        toast.success("✅ Fuente de datos creada exitosamente");
        
        // Pass the created datasource with ID back to parent
        // Keep the type as returned from backend (already in correct enum format)
        onSave({
          ...response.data,
          lastUpdate: new Date().toISOString().split("T")[0],
        });
        
        return; // Exit early for API REST
      } catch (error: any) {
        console.error("Error creating API REST datasource:", error);
        toast.error(error.response?.data?.message || "Error creando la fuente de datos");
        return;
      }
    }

    // Build configJson based on type (for non-API REST types)
    let configJson = form.configJson || {};
    if (form.type === "API REST") {
      // Build parameters object
      const parameters: any = {};
      
      const queryObj = keyValuePairsToObject(dynamicParams.query);
      if (Object.keys(queryObj).length > 0) parameters.query = queryObj;
      
      const pathObj = keyValuePairsToObject(dynamicParams.path);
      if (Object.keys(pathObj).length > 0) parameters.path = pathObj;
      
      const headersObj = keyValuePairsToObject(dynamicParams.headers);
      if (Object.keys(headersObj).length > 0) parameters.headers = headersObj;
      
      const bodyObj = keyValuePairsToObject(dynamicParams.body);
      if (Object.keys(bodyObj).length > 0) parameters.body = bodyObj;

      // Build auth object
      const auth = authConfig.type === 'bearer' && authConfig.token
        ? { type: 'bearer', token: authConfig.token }
        : undefined;

      configJson = {
        baseUrl: apiConfig.baseUrl,
        endpoint: apiConfig.endpoint,
        method: apiConfig.method,
        parameters: Object.keys(parameters).length > 0 ? parameters : undefined,
        auth,
      };
    }

    // Build save payload
    const savePayload: any = {
      id: form.id,
      name: form.name,
      description: form.description,
      type: TYPE_MAP[form.type] || form.type, // Map UI type to Prisma enum
      status: form.status ? (STATUS_MAP[form.status] || form.status) : "ACTIVE", // Map UI status to Prisma enum
      records: form.records || 0,
      fields: form.fields || 0,
      lastUpdate: form.lastUpdate || new Date().toISOString().split("T")[0],
    };

    // For Google Sheets, always use single configuration (multi-source DISABLED)
    if (form.type === 'Google Sheets') {
      // Single sheet configuration (ONLY option for MVP)
      const cleanedSpreadsheetId = extractSpreadsheetIdFromInput(form.spreadsheetId);
      savePayload.spreadsheetId = cleanedSpreadsheetId || form.spreadsheetId;
      savePayload.sheetName = form.sheetName;
      savePayload.range = form.range;
      
      // 🎯 Add business context if provided
      if (businessContext.trim()) {
        savePayload.configJson = { 
          spreadsheetId: cleanedSpreadsheetId || form.spreadsheetId,
          sheetName: form.sheetName,
          range: form.range,
          businessContext: businessContext.trim() 
        };
      }
      
      console.log('💾 Saving single Google Sheet:', cleanedSpreadsheetId);
    } else {
      // For API sources, send configJson
      savePayload.configJson = configJson;
    }

    // Pass only necessary fields (avoid sending heavy analysis data)
    
    setSaving(true);
    try {
      console.log('🚀 Starting save with auto-analysis');
      
      let result: any;
      
      if (form.id) {
        // Update existing datasource
        console.log('📝 Updating existing datasource:', form.id);
        
        // For UPDATE: Single sheet configuration only (multi-source DISABLED)
        if (form.type === 'Google Sheets') {
          // Single sheet configuration for UPDATE (ONLY option)
          const cleanedSpreadsheetId = extractSpreadsheetIdFromInput(form.spreadsheetId);
          
          savePayload.config = {
            type: 'GOOGLE_SHEETS',
            spreadsheetId: cleanedSpreadsheetId || form.spreadsheetId,
            sheetName: form.sheetName || 'Sheet1',
            range: form.range || '',
            businessContext: businessContext.trim() || undefined // 🎯 Include business context
          };
          
          // Also set top-level fields for backward compatibility
          savePayload.spreadsheetId = savePayload.config.spreadsheetId;
          savePayload.sheetName = savePayload.config.sheetName;
          savePayload.range = savePayload.config.range;
          savePayload.configJson = savePayload.config; // Ensure consistency
          
          console.log('📝 UPDATE: Single Google Sheet config:', savePayload.config);
        } else if (form.type === 'API REST') {
          // API REST configuration for UPDATE
          savePayload.config = configJson;
          savePayload.configJson = configJson;
        }
        
        result = await api.put(`/datasources/${form.id}`, savePayload);
        
        // After update, trigger re-analysis
        console.log('🔄 Re-analyzing updated datasource...');
        const reAnalysisResult = await api.post(`/datasources/${form.id}/analyze`);
        
        // Merge the analysis results
        result.data.analysis = reAnalysisResult.data;
        result.data.autoAnalyzed = true;
        
      } else {
        // Create new datasource (with auto-analysis)
        console.log('🆕 Creating new datasource with auto-analysis');
        result = await api.post("/datasources", savePayload);
        
        // Backend auto-analyzes, but response may come before analysis completes
        // If status is not ACTIVE yet, wait a bit and refetch
        if (result.data.status !== 'ACTIVE' && result.data.flow === 'AUTO_ANALYSIS') {
          console.log('⏳ Waiting for backend auto-analysis to complete...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Refetch to get updated status
          const refreshed = await api.get(`/datasources/${result.data.id}`);
          result.data = refreshed.data;
          console.log('🔄 Refreshed datasource:', result.data);
        }
      }

      console.log('✅ Save result:', result.data);

      // Handle successful save with analysis
      const hasAnalysis = result.data.analysis && result.data.analysis.rowCount > 0;
      const isActive = result.data.status === 'ACTIVE';
      
      if (hasAnalysis && isActive) {
        const analysis = result.data.analysis;
        
        // Update form state with analyzed data
        setForm(prev => ({
          ...prev,
          id: result.data.id,
          records: analysis.rowCount || 0,
          fields: analysis.columns?.length || 0,
        }));
        
        setAnalyzed(true);
        toast.success(`✅ Fuente analizada: ${analysis.rowCount} filas, ${analysis.columns?.length || 0} campos`);
        
      } else if (result.data.status === 'SUSPENDED') {
        setForm(prev => ({ ...prev, id: result.data.id }));
        toast.error("Error en análisis automático. Intenta analizar manualmente.");
      } else {
        setForm(prev => ({ ...prev, id: result.data.id }));
        toast.success("Fuente creada, análisis pendiente");
      }

      // Call parent onSave callback
      onSave(result.data);
      
    } catch (error: any) {
      console.error("Error saving datasource:", error);
      const errorMessage = error.response?.data?.message || error.message || "Error guardando la fuente de datos";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 overflow-y-auto p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-3xl p-4 md:p-6 shadow-lg my-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-white mb-4">
          {datasource ? "Editar Fuente de Datos" : "Nueva Fuente de Datos"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Nombre</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Descripción</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 min-h-[80px]"
            />
          </div>

          {/* 🎯 Business Context Field (Optional) */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Propósito de la fuente de datos
              <span className="text-slate-500 text-xs ml-2">(opcional - ayuda a generar mejores dashboards)</span>
            </label>
            <textarea
              value={businessContext}
              onChange={(e) => setBusinessContext(e.target.value)}
              placeholder="Ej: Esta fuente contiene ventas diarias de todos los locales. Se usa para analizar tendencias de facturación, identificar productos top y comparar performance entre sucursales."
              className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 min-h-[100px] text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              💡 Al describir el propósito, la IA generará gráficos más relevantes y accionables
            </p>
          </div>

          {/* 🎯 TIPO DE FUENTE: Read-only badge - el tipo NO se puede cambiar */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">Tipo de Fuente</label>
            <div className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              {form.type === "Google Sheets" && (
                <FileSpreadsheet className="w-5 h-5 text-sky-400" />
              )}
              {form.type === "API REST" && (
                <Globe className="w-5 h-5 text-blue-400" />
              )}
              <span className="text-white font-medium">{form.type}</span>
              <span className="ml-auto text-xs text-slate-500">(No modificable)</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              💡 El tipo de fuente se define al crear y no puede cambiarse después
            </p>
          </div>

          {form.type === "Google Sheets" && (
            <div className="space-y-4 bg-slate-800/50 p-4 rounded-lg border border-sky-500/20">
              <div className="flex items-center justify-between">
                <h3 className="text-sky-400 font-semibold">Configuración de Google Sheets</h3>
              </div>

              {/* SINGLE GOOGLE SHEET CONFIGURATION ONLY */}
              <>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">
                    Spreadsheet ID o URL
                    <span className="text-slate-500 text-xs ml-2">(requerido)</span>
                  </label>
                  <input
                    type="text"
                    name="spreadsheetId"
                    value={form.spreadsheetId}
                    onChange={handleChange}
                    placeholder="1ABC123XYZ456 o https://docs.google.com/spreadsheets/d/..."
                    className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Nombre de la pestaña</label>
                    <input
                      type="text"
                      name="sheetName"
                      value={form.sheetName}
                      onChange={handleChange}
                      placeholder="Sheet1"
                      className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-300 mb-1">
                      Rango <span className="text-slate-500 text-xs">(opcional)</span>
                    </label>
                    <input
                      type="text"
                      name="range"
                      value={form.range}
                      onChange={handleChange}
                      placeholder="A1:Z1000"
                      className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                {/* Toggle para m\u00faltiples hojas */}
                {ENABLE_MULTI_SOURCE_UI && (
                  <div className="pt-3 border-t border-slate-700">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={isMultipleSheets}
                        onChange={(e) => {
                          setIsMultipleSheets(e.target.checked);
                          if (e.target.checked) {
                            // Inicializar con la primera hoja si tiene datos
                            setGoogleSheets([{
                              id: Date.now().toString(),
                              spreadsheetId: form.spreadsheetId || '',
                              sheetName: form.sheetName || 'Sheet1',
                              range: form.range || '',
                              alias: ''
                            }]);
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-sky-500 focus:ring-2 focus:ring-sky-500 cursor-pointer"
                      />
                      <span className="text-sm text-slate-300">
                        Agregar multiples hojas del mismo Spreadsheet
                      </span>
                    </label>
                    <p className="text-xs text-slate-500 mt-1 ml-6">
                      Puedes combinar datos de varias pestañas en un solo dataset
                    </p>
                  </div>
                )}

                {/* UI para múltiples hojas */}
                {isMultipleSheets && ENABLE_MULTI_SOURCE_UI && (
                  <div className="space-y-3 pt-4 border-t border-sky-500/30">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sky-400 font-semibold text-sm">Hojas del Spreadsheet</h4>
                      <button
                        type="button"
                        onClick={() => {
                          setGoogleSheets([...googleSheets, {
                            id: Date.now().toString(),
                            spreadsheetId: form.spreadsheetId || '',
                            sheetName: '',
                            range: '',
                            alias: ''
                          }]);
                        }}
                        className="flex items-center gap-1 px-3 py-1 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded transition"
                      >
                        <span>\u2795</span>
                        Agregar Hoja
                      </button>
                    </div>

                    {googleSheets.map((sheet, index) => (
                      <div key={sheet.id} className="bg-slate-900/50 p-3 rounded-lg border border-slate-700 space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-sky-400">Hoja #{index + 1}</span>
                          {googleSheets.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                setGoogleSheets(googleSheets.filter(s => s.id !== sheet.id));
                              }}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              \u274c Eliminar
                            </button>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Nombre de la pestaña</label>
                          <input
                            type="text"
                            value={sheet.sheetName}
                            onChange={(e) => {
                              const updated = googleSheets.map(s =>
                                s.id === sheet.id ? { ...s, sheetName: e.target.value } : s
                              );
                              setGoogleSheets(updated);
                            }}
                            placeholder="Sheet1"
                            className="w-full bg-slate-800 text-white px-2 py-1.5 text-sm rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Rango (opcional)</label>
                          <input
                            type="text"
                            value={sheet.range}
                            onChange={(e) => {
                              const updated = googleSheets.map(s =>
                                s.id === sheet.id ? { ...s, range: e.target.value } : s
                              );
                              setGoogleSheets(updated);
                            }}
                            placeholder="A1:Z1000 (dejar vac\u00edo para toda la hoja)"
                            className="w-full bg-slate-800 text-white px-2 py-1.5 text-sm rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Alias (opcional)</label>
                          <input
                            type="text"
                            value={sheet.alias}
                            onChange={(e) => {
                              const updated = googleSheets.map(s =>
                                s.id === sheet.id ? { ...s, alias: e.target.value } : s
                              );
                              setGoogleSheets(updated);
                            }}
                            placeholder="Nombre descriptivo para esta hoja"
                            className="w-full bg-slate-800 text-white px-2 py-1.5 text-sm rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          />
                        </div>
                      </div>
                    ))}

                    <div className="text-xs text-slate-500 bg-blue-500/10 border border-blue-500/30 rounded p-2">
                      <span className="text-blue-400 font-semibold">Nota:</span> Todas las hojas deben pertenecer al mismo Spreadsheet ID configurado arriba.
                    </div>
                  </div>
                )}
              </>
            </div>
          )}

          {form.type === "API REST" && (
            <div className="space-y-6">
              {/* 🎯 PASO 1: SELECTOR PRINCIPAL DE TIPO DE API */}
              <div className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
                <h3 className="text-blue-400 font-semibold text-lg flex items-center gap-2 mb-4">
                  <span>🎯</span> Paso 1: Tipo de API
                </h3>
                <div className="flex gap-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="apiType"
                      value="public"
                      checked={apiType === 'public'}
                      onChange={(e) => {
                        setApiType(e.target.value as 'public' | 'private');
                        if (e.target.value === 'public') {
                          setAuthPhase('data_config'); // Skip auth for public APIs
                        } else {
                          setAuthPhase('configure');
                        }
                      }}
                      className="text-sky-500 focus:ring-sky-500"
                    />
                    <div>
                      <span className="text-sky-400 font-semibold">🌍 API Pública</span>
                      <p className="text-xs text-slate-400">Sin autenticación requerida</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="apiType"
                      value="private"
                      checked={apiType === 'private'}
                      onChange={(e) => {
                        setApiType(e.target.value as 'public' | 'private');
                        setAuthPhase('configure');
                      }}
                      className="text-blue-500 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-blue-400 font-semibold">🔐 API Privada</span>
                      <p className="text-xs text-slate-400">Requiere autenticación</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* 🔐 PASO 2: SELECTOR DE MODO DE AUTENTICACIÓN (Solo para API privadas) */}
              {apiType === 'private' && (
                <div className="bg-slate-800/50 p-6 rounded-lg border border-purple-500/20">
                  <h3 className="text-purple-400 font-semibold text-lg flex items-center gap-2 mb-4">
                    <span>🔧</span> Paso 2: Modo de Autenticación
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded border border-sky-500/30 bg-sky-500/5">
                      <input
                        type="radio"
                        name="privateApiMode"
                        value="simple"
                        checked={privateApiMode === 'simple'}
                        onChange={(e) => {
                          setPrivateApiMode(e.target.value as 'simple' | 'oauth_formal');
                          setAuthPhase('configure');
                          setSavedAuthToken(null);
                        }}
                        className="text-sky-500 focus:ring-sky-500 mt-1"
                      />
                      <div className="flex-1">
                        <span className="text-sky-400 font-semibold">🔹 Token Simple (Recomendado)</span>
                        <p className="text-xs text-slate-400 mt-1">
                          "Tengo una URL que devuelve un token" - Compatible con APIs como Finneg
                        </p>
                        <div className="text-xs text-sky-300 mt-2 p-2 bg-sky-500/10 rounded">
                          ✓ Configurable como Postman ✓ Cualquier método GET/POST ✓ Parámetros flexibles
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded border border-blue-500/30 bg-blue-500/5">
                      <input
                        type="radio"
                        name="privateApiMode"
                        value="oauth_formal"
                        checked={privateApiMode === 'oauth_formal'}
                        onChange={(e) => {
                          setPrivateApiMode(e.target.value as 'simple' | 'oauth_formal');
                          setAuthPhase('configure');
                          setSavedAuthToken(null);
                        }}
                        className="text-blue-500 focus:ring-blue-500 mt-1"
                      />
                      <div className="flex-1">
                        <span className="text-blue-400 font-semibold">🔹 OAuth Formal</span>
                        <p className="text-xs text-slate-400 mt-1">
                          "Tengo client_id, client_secret y token endpoint estándar OAuth"
                        </p>
                        <div className="text-xs text-blue-300 mt-2 p-2 bg-blue-500/10 rounded">
                          ✓ OAuth Client Credentials ✓ Estándar de industria ✓ Renovación automática
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* 🔐 FASE DE CONFIGURACIÓN DE AUTENTICACIÓN */}
              {apiType === 'private' && authPhase === 'configure' && (
                <div className="bg-red-900/20 p-6 rounded-lg border border-red-500/30">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-red-400 font-semibold text-lg flex items-center gap-2">
                      <span>🔑</span> Configurar Autenticación
                      {privateApiMode === 'simple' ? ' - Token Simple' : ' - OAuth Formal'}
                    </h3>
                  </div>

                  {privateApiMode === 'simple' ? (
                    /* 🔹 TOKEN SIMPLE UI (Postman-style) */
                    <div className="space-y-4">
                      <div className="bg-sky-500/10 border border-sky-500/30 rounded p-3 mb-4">
                        <p className="text-sky-400 text-sm font-semibold">📋 Configuración tipo Postman</p>
                        <p className="text-xs text-slate-300 mt-1">
                          Configura la request que devuelve el token. El sistema buscará automáticamente 'access_token', 'token' o 'auth_token' en la respuesta.
                        </p>
                      </div>

                      {/* Method + URL */}
                      <div className="flex gap-2">
                        <select
                          value={simpleAuthConfig.method}
                          onChange={(e) => setSimpleAuthConfig(prev => ({ ...prev, method: e.target.value as 'GET' | 'POST' }))}
                          className="bg-slate-800 text-white px-4 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 font-semibold"
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                        </select>
                        <input
                          type="text"
                          value={simpleAuthConfig.tokenUrl}
                          onChange={(e) => setSimpleAuthConfig(prev => ({ ...prev, tokenUrl: e.target.value }))}
                          placeholder="https://api.finneg.com/connect/token"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                        />
                      </div>

                      {/* Postman-style tabs for parameters */}
                      <div className="border-b border-slate-700">
                        <div className="flex gap-1">
                          {(['query', 'headers', 'body'] as const).map(tab => (
                            <button
                              key={tab}
                              type="button"
                              onClick={() => setActiveAuthTab(tab as 'query' | 'headers' | 'body')}
                              className={`px-4 py-2 text-sm font-medium transition-colors ${
                                activeAuthTab === tab
                                  ? 'text-sky-400 border-b-2 border-sky-400'
                                  : 'text-slate-400 hover:text-slate-300'
                              }`}
                            >
                              {tab === 'query' ? 'Query' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                              {simpleAuthConfig[tab === 'query' ? 'queryParams' : tab].filter((p: KeyValuePair) => p.enabled).length > 0 && (
                                <span className="ml-1 text-xs bg-sky-500/20 px-1.5 py-0.5 rounded">
                                  {simpleAuthConfig[tab === 'query' ? 'queryParams' : tab].filter((p: KeyValuePair) => p.enabled).length}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Parameters content (simplified for now) */}
                      <div className="min-h-[120px] p-4 bg-slate-900 rounded">
                        <p className="text-slate-400 text-sm">
                          {activeAuthTab === 'query' && 'Query parameters: ?key=value&another=param'}
                          {activeAuthTab === 'headers' && 'HTTP Headers: Content-Type, Authorization, etc.'}
                          {activeAuthTab === 'body' && 'Request body data (para POST)'}
                        </p>
                      </div>

                      {/* Test Simple Auth Button */}
                      <button
                        type="button"
                        onClick={handleTestTokenSimple}
                        disabled={testingApi || !simpleAuthConfig.tokenUrl}
                        className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-slate-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                      >
                        {testingApi ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                            </svg>
                            Obteniendo token...
                          </>
                        ) : (
                          <>
                            <span>🔑</span>
                            Obtener Token
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    /* 🔹 OAUTH FORMAL UI (Standard) */
                    <div className="space-y-4">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 mb-4">
                        <p className="text-blue-400 text-sm font-semibold">🔐 OAuth Client Credentials</p>
                        <p className="text-xs text-slate-300 mt-1">
                          Configuración estándar OAuth con client_id, client_secret y token endpoint.
                        </p>
                      </div>

                      <input
                        type="text"
                        value={oauthConfig.tokenUrl}
                        onChange={(e) => setOauthConfig(prev => ({ ...prev, tokenUrl: e.target.value }))}
                        placeholder="https://api.example.com/oauth/token"
                        className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={oauthConfig.clientId}
                          onChange={(e) => setOauthConfig(prev => ({ ...prev, clientId: e.target.value }))}
                          placeholder="Client ID"
                          className="bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="password"
                          value={oauthConfig.clientSecret}
                          onChange={(e) => setOauthConfig(prev => ({ ...prev, clientSecret: e.target.value }))}
                          placeholder="Client Secret"
                          className="bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <input
                        type="text"
                        value={oauthConfig.scope}
                        onChange={(e) => setOauthConfig(prev => ({ ...prev, scope: e.target.value }))}
                        placeholder="Scope (opcional)"
                        className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />

                      {/* Test OAuth Button */}
                      <button
                        type="button"
                        onClick={handleTestOAuthFormal}
                        disabled={testingApi || !oauthConfig.tokenUrl || !oauthConfig.clientId || !oauthConfig.clientSecret}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                      >
                        {testingApi ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                            </svg>
                            Autenticando OAuth...
                          </>
                        ) : (
                          <>
                            <span>🔐</span>
                            Autenticar OAuth
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ✅ TOKEN VALIDADO - RESUMEN */}
              {apiType === 'private' && authPhase === 'validated' && savedAuthToken && (
                <div className="bg-sky-500/20 border border-sky-500/30 rounded p-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sky-400 font-semibold">
                      Autenticación exitosa - {privateApiMode === 'simple' ? 'Token Simple' : 'OAuth Formal'}
                    </p>
                  </div>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-slate-300">
                      Token: {savedAuthToken.substring(0, 20)}...
                    </p>
                    {tokenExpiration && (
                      <p className="text-xs text-slate-400">
                        Expira: {tokenExpiration.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setAuthPhase('data_config')}
                    className="mt-3 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded text-sm font-semibold"
                  >
                    ➡️ Configurar API de Datos
                  </button>
                </div>
              )}

              {/* 📊 CONFIGURACIÓN DE API DE DATOS */}
              {(apiType === 'public' || authPhase === 'data_config') && (
                <div className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
                  <h3 className="text-blue-400 font-semibold text-lg flex items-center gap-2 mb-4">
                    <span>📊</span> 
                    {apiType === 'private' ? 'Paso 3: ' : ''}Configurar API de Datos
                  </h3>
              
              {/* Request URL Section */}
              <div className="space-y-3 bg-slate-900 p-4 rounded-lg">
                <div className="flex gap-2">
                  <select
                    value={apiConfig.method}
                    onChange={(e) => handleApiConfigChange('method', e.target.value)}
                    className="bg-slate-800 text-white px-4 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                    <option value="PATCH">PATCH</option>
                  </select>

                  <div className="flex-1">
                    <input
                      type="text"
                      value={apiConfig.baseUrl}
                      onChange={(e) => handleApiConfigChange('baseUrl', e.target.value)}
                      placeholder="https://api.example.com"
                      className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {/* Type Detection Badge */}
                    {detectedType && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-slate-400">Tipo detectado:</span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-xs font-semibold">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {detectedType}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <input
                    type="text"
                    value={apiConfig.endpoint}
                    onChange={(e) => handleApiConfigChange('endpoint', e.target.value)}
                    placeholder="/users/:id"
                    className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Preview URL */}
                {buildPreviewUrl() && (
                  <div className="bg-slate-800/50 p-3 rounded border border-sky-500/30">
                    <p className="text-xs text-slate-400 mb-1">URL final:</p>
                    <p className="text-sky-400 text-sm font-mono break-all">{buildPreviewUrl()}</p>
                  </div>
                )}
              </div>

              {/* Tabs for Parameters */}
              <div className="border-b border-slate-700 overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                  {(['query', 'path', 'headers', 'body'] as const).map(tab => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveParamTab(tab)}
                      className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                        activeParamTab === tab
                          ? 'text-blue-400 border-b-2 border-blue-400'
                          : 'text-slate-400 hover:text-slate-300'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      {dynamicParams[tab as keyof DynamicParams].filter(p => p.enabled).length > 0 && (
                        <span className="ml-1 text-xs bg-blue-500/20 px-1.5 py-0.5 rounded">
                          {dynamicParams[tab as keyof DynamicParams].filter(p => p.enabled).length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Parameters Content */}
              <div className="min-h-[200px]">
                {/* Query Params Tab */}
                {activeParamTab === 'query' && (
                  <div className="space-y-2">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 mb-3">
                      <p className="text-xs text-slate-300">
                        Query parameters: <span className="text-blue-400">?key=value&foo=bar</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        💡 Use variables: <span className="text-sky-400 font-mono">{'{access_token}'}</span>, <span className="text-sky-400 font-mono">{'{empresa}'}</span>
                      </p>
                    </div>
                    {dynamicParams.query.map(param => (
                      <div key={param.id} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                        <input
                          type="checkbox"
                          checked={param.enabled}
                          onChange={(e) => updateParam('query', param.id, 'enabled', e.target.checked)}
                          className="w-4 h-4 bg-slate-800 border-slate-600 rounded self-start sm:self-center"
                        />
                        <input
                          type="text"
                          value={param.key}
                          onChange={(e) => updateParam('query', param.id, 'key', e.target.value)}
                          placeholder="key"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <input
                          type="text"
                          value={param.value}
                          onChange={(e) => updateParam('query', param.id, 'value', e.target.value)}
                          placeholder="value or {access_token}"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeParam('query', param.id)}
                          className="text-red-400 hover:text-red-300 px-2 self-end sm:self-center"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addParam('query')}
                      className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                    >
                      + Add Query Param
                    </button>
                  </div>
                )}

                {/* Path Params Tab */}
                {activeParamTab === 'path' && (
                  <div className="space-y-2">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 mb-3">
                      <p className="text-xs text-slate-300">
                        Path parameters: <span className="text-blue-400">/users/:id</span> → <span className="text-sky-400">/users/123</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        💡 Supports <span className="text-sky-400 font-mono">:param</span> and <span className="text-sky-400 font-mono">{'{param}'}</span> syntax
                      </p>
                    </div>
                    {dynamicParams.path.map(param => (
                      <div key={param.id} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                        <input
                          type="checkbox"
                          checked={param.enabled}
                          onChange={(e) => updateParam('path', param.id, 'enabled', e.target.checked)}
                          className="w-4 h-4 bg-slate-800 border-slate-600 rounded self-start sm:self-center"
                        />
                        <input
                          type="text"
                          value={param.key}
                          onChange={(e) => updateParam('path', param.id, 'key', e.target.value)}
                          placeholder=":id"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <input
                          type="text"
                          value={param.value}
                          onChange={(e) => updateParam('path', param.id, 'value', e.target.value)}
                          placeholder="123 or {empresa}"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeParam('path', param.id)}
                          className="text-red-400 hover:text-red-300 px-2 self-end sm:self-center"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addParam('path')}
                      className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                    >
                      + Add Path Param
                    </button>
                  </div>
                )}

                {/* Headers Tab */}
                {activeParamTab === 'headers' && (
                  <div className="space-y-2">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 mb-3">
                      <p className="text-xs text-slate-300">
                        Custom headers to include in the request
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        💡 Use variables: <span className="text-sky-400 font-mono">{'{access_token}'}</span> in values
                      </p>
                    </div>
                    {dynamicParams.headers.map(param => (
                      <div key={param.id} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                        <input
                          type="checkbox"
                          checked={param.enabled}
                          onChange={(e) => updateParam('headers', param.id, 'enabled', e.target.checked)}
                          className="w-4 h-4 bg-slate-800 border-slate-600 rounded self-start sm:self-center"
                        />
                        <input
                          type="text"
                          value={param.key}
                          onChange={(e) => updateParam('headers', param.id, 'key', e.target.value)}
                          placeholder="X-API-Key"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <input
                          type="text"
                          value={param.value}
                          onChange={(e) => updateParam('headers', param.id, 'value', e.target.value)}
                          placeholder="your-api-key or {access_token}"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeParam('headers', param.id)}
                          className="text-red-400 hover:text-red-300 px-2 self-end sm:self-center"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addParam('headers')}
                      className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                    >
                      + Add Header
                    </button>
                  </div>
                )}

                {/* Body Params Tab */}
                {activeParamTab === 'body' && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400 mb-3">
                      Body parameters for POST/PUT/PATCH requests (will be converted to JSON)
                    </p>
                    {dynamicParams.body.map(param => (
                      <div key={param.id} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                        <input
                          type="checkbox"
                          checked={param.enabled}
                          onChange={(e) => updateParam('body', param.id, 'enabled', e.target.checked)}
                          className="w-4 h-4 bg-slate-800 border-slate-600 rounded self-start sm:self-center"
                        />
                        <input
                          type="text"
                          value={param.key}
                          onChange={(e) => updateParam('body', param.id, 'key', e.target.value)}
                          placeholder="key"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <input
                          type="text"
                          value={param.value}
                          onChange={(e) => updateParam('body', param.id, 'value', e.target.value)}
                          placeholder="value"
                          className="flex-1 bg-slate-800 text-white px-3 py-2 rounded border border-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeParam('body', param.id)}
                          className="text-red-400 hover:text-red-300 px-2 self-end sm:self-center"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addParam('body')}
                      className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                    >
                      + Add Body Param
                    </button>
                  </div>
                )}
              </div>

              {/* Test Connection Button */}
              <button
                type="button"
                onClick={handleTestApiConnection}
                disabled={testingApi || !apiConfig.baseUrl.trim() || !apiConfig.endpoint.trim()}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-semibold flex items-center justify-center gap-2"
              >
                {testingApi ? "⏳ Probando conexión..." : apiTestSuccess ? "✓ Conexión exitosa - Probar de nuevo" : "🔍 Probar Conexión"}
              </button>

              {/* API Test Result */}
              {apiTestSuccess && apiPreview.length > 0 && (
                <div className="mt-4 space-y-3">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-blue-400 font-semibold">✓ Conexión exitosa</p>
                    <p className="text-slate-300 text-sm mt-1">
                      API respondió correctamente. Preview de {apiPreview.length} registro(s)
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-300 text-sm font-semibold mb-2">Preview de datos:</p>
                    <div className="bg-slate-800 rounded-lg p-3 max-h-60 overflow-auto text-xs">
                      <pre className="text-slate-400">
                        {JSON.stringify(apiPreview, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
                </div>
              )}
            </div>
          )}

          {form.type !== "Google Sheets" && form.type !== "API REST" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Registros</label>
                <input
                  type="number"
                  name="records"
                  value={form.records}
                  onChange={handleChange}
                  className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1">Campos</label>
                <input
                  type="number"
                  name="fields"
                  value={form.fields}
                  onChange={handleChange}
                  className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-slate-700 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`w-full sm:w-auto px-4 py-2 rounded-lg transition-colors ${
                saving 
                  ? 'bg-sky-400 cursor-not-allowed text-white' 
                  : 'bg-sky-600 hover:bg-sky-700 text-white'
              }`}
            >
              {saving ? 'Guardando y analizando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DatasourceForm;
